import React, { createContext, useContext, useState, useCallback } from 'react';

// Locale values are translation-dictionary keys persisted as preferences,
// not inferred country or currency defaults.
export type Language = 'en-UG' | 'en-US' | 'fr' | 'sw' | 'ar';

const STORAGE_KEY = 'th.locale';
const VALID_LANGS: Language[] = ['en-UG', 'en-US', 'fr', 'sw', 'ar'];
const RTL_LANGS: Language[] = ['ar'];

// eslint-disable-next-line react-refresh/only-export-components -- error fallbacks can render above the provider
export function readStoredLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Language | null;
    if (stored && VALID_LANGS.includes(stored)) return stored;
  } catch {
    // localStorage not available (SSR / private mode)
  }
  return 'en-UG';
}

function applyDocumentLanguage(lang: Language) {
  document.documentElement.lang = lang;
  document.documentElement.dir = RTL_LANGS.includes(lang) ? 'rtl' : 'ltr';
}

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  getLanguageLabel: (lang: Language) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

LanguageContext.displayName = 'LanguageContext';

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const lang = readStoredLanguage();
    applyDocumentLanguage(lang);
    return lang;
  });

  const setLanguage = useCallback((lang: Language) => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
    applyDocumentLanguage(lang);
    setLanguageState(lang);
  }, []);

  const getLanguageLabel = (lang: Language): string => {
    switch (lang) {
      case 'en-UG': return 'English';
      case 'en-US': return 'English (US)';
      case 'fr': return 'French';
      case 'sw': return 'Swahili';
      case 'ar': return 'Arabic';
      default: return 'English';
    }
  };

  const value = React.useMemo(
    () => ({ language, setLanguage, getLanguageLabel }),
    [language, setLanguage]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

LanguageProvider.displayName = 'LanguageProvider';

// eslint-disable-next-line react-refresh/only-export-components -- co-located context hook; splitting would ripple across all consumers
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

/** Base-language fallback for leaf copy hooks rendered outside the app shell. */
// eslint-disable-next-line react-refresh/only-export-components -- co-located context hook
export function useOptionalLanguage() {
  return useContext(LanguageContext);
}
